// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Dictionary } from "@insite/client-framework/Common/Types";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { AccountSettingsModel } from "@insite/client-framework/Types/ApiModels";
import {
    faxPattern,
    longZipCodePattern,
    phonePattern,
    shortZipCodePattern,
    TextMask,
} from "@keller/App/Libraries/react-text-mask";
import { xRegex } from "@keller/XDL/Utilities/validation";
import { Dispatch, SetStateAction } from "react";

const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const digitRegExp = /[0-9]/;
const lowerRegExp = /[a-z]/;
const upperRegExp = /[A-Z]/;
const specialRegExp = /\W/;

const maxFileSize = 15 * 1024 * 1024; // 15MB
const validFileTypes = ["pdf"];

const requiredMessage = siteMessage("Keller_Field_Required");

export type ErrorResult = boolean | typeof requiredMessage;

export type FormValidator = (value: any, options?: any) => ErrorResult;

export interface FormValidators {
    required: FormValidator;
    email: FormValidator;
    password: FormValidator;
    confirmPassword: FormValidator;
    file: FormValidator;
    phone: FormValidator;
    zipCode: FormValidator;
    fax: FormValidator;
}

export const validators: FormValidators = {
    required: (value: any) => {
        if (value) {
            return false;
        }
        return siteMessage("Keller_Field_Required");
    },
    email: (value: string) => {
        if (!value || emailRegexp.test(value)) {
            return false;
        }
        return siteMessage("Keller_EmailAddress_ValidEmail");
    },
    password: (password: string, options?: { accountSettings?: AccountSettingsModel; confirmPassword?: string }) => {
        if (!options?.accountSettings) {
            return "";
        }

        // We get it from validatePassword handler
        const {
            passwordMinimumLength,
            passwordRequiresDigit,
            passwordRequiresLowercase,
            passwordRequiresUppercase,
            passwordRequiresSpecialCharacter,
        } = options.accountSettings;

        if (!password) {
            return siteMessage("CreateNewAccountInfo_Password_Required").toString();
        }

        if (password.length > 0 && password.length < passwordMinimumLength) {
            return translate("Password must be at least {0} characters long", passwordMinimumLength.toString());
        }

        if (passwordRequiresDigit && !digitRegExp.test(password)) {
            return translate("Password must include at least one number");
        }

        if (passwordRequiresLowercase && !lowerRegExp.test(password)) {
            return translate("Password must include at least one lowercase character");
        }

        if (passwordRequiresUppercase && !upperRegExp.test(password)) {
            return translate("Password must include at least one uppercase character");
        }

        if (passwordRequiresSpecialCharacter && !specialRegExp.test(password)) {
            return translate("Password must include at least one non alphanumeric character");
        }
        if (password !== options.confirmPassword) {
            return siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch").toString();
        }

        return false;
    },
    confirmPassword: (confirmPassword: string, options?: { store?: ApplicationState; password?: string }) => {
        return confirmPassword !== options?.password
            ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch").toString()
            : false;
    },
    file: (file: File) => {
        if (file) {
            const fileExtension = getFileExtension(file.name);
            if (validFileTypes.indexOf(fileExtension) === -1) {
                return siteMessage("Keller_File_InValidFileType");
            }
            if (file.size > maxFileSize) {
                return siteMessage("Keller_File_InValidFileSize");
            }
        }
        return false;
    },
    phone: (value: string) => {
        if (!xRegex.phone.test(value)) {
            return siteMessage("Keller_Phone_InValidPhoneFormat");
        }
        return false;
    },
    zipCode: (value: string, options?: { country: string }) => {
        const country = options ? options.country : "US";
        if (
            country === "US" &&
            value &&
            value.length !== shortZipCodePattern.length &&
            value.length !== longZipCodePattern.length
        ) {
            return siteMessage("Keller_ZipCode_InValidFormat");
        }
        return false;
    },
    fax: (value: string) => {
        if (value && value.length !== faxPattern.length) {
            return siteMessage("Keller_Fax_InValidFaxFormat");
        }
        return false;
    },
};

export type ValidatorName = keyof FormValidators;

export interface FieldAttributes {
    id?: string;
    name?: string;
    type?: string;
    maxLength?: number;
    required?: boolean;
    label?: string;
    placeholder?: string;
    dateTimePickerProps?: any;
}

export interface FormState {
    isValid: boolean;
    isSubmitted: boolean;
    fields: Dictionary<FormField<any>>;
}

export type FormField<T> = {
    value: T;
    validators?: ValidatorName[] | ((formState: FormState, options?: { [key: string]: any }) => ValidatorName[]);
    error?: ErrorResult;
    mask?: TextMask;
    attrs?: FieldAttributes;
    validatorsOptions?: (formState: FormState) => { [key: string]: any };
};

export class CreateForm<T extends FormState> {
    state: T;
    setState: Dispatch<SetStateAction<T>>;
    options?: { [key: string]: any };

    constructor(state: T, setState: Dispatch<SetStateAction<T>>, options?: { [key: string]: any }) {
        this.state = state;
        this.setState = setState;
        this.options = options;
        this.validate();
    }

    public validate = () => {
        let result = true;
        for (const fieldKey in this.state.fields) {
            const field: FormField<any> = this.state.fields[fieldKey] as any;
            const validatorsOptions =
                typeof field.validatorsOptions === "function" ? field.validatorsOptions(this.state) : {};
            const validatorKeys =
                typeof field.validators === "function" ? field.validators(this.state, this.options) : field.validators;
            if (validatorKeys && validatorKeys.length > 0) {
                validatorKeys.every((key: ValidatorName) => {
                    field.error = validators[key](field.value, { ...this.options, ...validatorsOptions });
                    result = result && !field.error;
                    return !field.error;
                });
            } else {
                field.error = false;
            }
        }

        this.state.isValid = result;

        return result;
    };

    public updateState() {
        this.setState({ ...this.state });
    }

    public onFieldChange(field: FormField<any>, newValue: any) {
        if (field.mask) {
            field.value = field.mask(newValue, field.value);
        } else {
            field.value = newValue;
        }
        this.validate();
        this.updateState();
    }

    public setSubmitted() {
        this.setState({ ...this.state, isSubmitted: true } as T);
    }

    public setPrestine() {
        this.setState({ ...this.state, isSubmitted: false } as T);
    }
}
